'use client'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { MC, MOTION, SERIF, SANS } from '@/lib/mycio/theme'
import { useMyCIOLanguage } from '@/lib/mycio/MyCIOLanguageContext'
import { getMyCIOCopy } from '@/lib/mycio/translations'
import { useMounted } from './hooks'

/**
 * Hero cross-dissolve. Cycles all six README questions: hold ~5s, fade the current H1 + paired
 * lead out (heroFadeOut 0.74s), advance the index after 760ms, dissolve the next in
 * (heroDissolve 1.5s, lead +0.1s delay). setInterval 6000ms; timers cleared on unmount. Under
 * reduced-motion the rotation never starts (first item holds).
 */
function useHeroRotation(questionCount: number) {
  const [idx, setIdx] = useState(0)
  const [phase, setPhase] = useState<'in' | 'out'>('in')
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setIdx(0)
    setPhase('in')
  }, [questionCount])

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce) return

    intervalRef.current = setInterval(() => {
      setPhase('out')
      timeoutRef.current = setTimeout(() => {
        setIdx((i) => (i + 1) % questionCount)
        setPhase('in')
      }, MOTION.heroFadeOutMs + 20)
    }, MOTION.heroHoldMs)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [questionCount])

  return { idx, phase }
}

function PlanProofCard({ copy }: { copy: ReturnType<typeof getMyCIOCopy> }) {
  const mounted = useMounted()
  // Bars start at width 0 and fill to target after mount, per the handoff's 1.1s transition.
  const bars = (w: string) => ({
    width: mounted ? w : '0%',
    height: '100%',
    borderRadius: 999,
    transition: 'width 1.1s cubic-bezier(0.22,1,0.36,1)',
  })
  return (
    <div style={{ flex: '1 1 380px', minWidth: 320 }} data-reveal>
      <div
        style={{
          background: MC.surface,
          border: `1px solid ${MC.border}`,
          borderRadius: 16,
          padding: '26px 26px 22px',
          boxShadow: '0 24px 60px -34px rgba(35,33,28,0.4)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontSize: 11.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: MC.faint, fontWeight: 600 }}>
            {copy.planCard.title}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: MC.muted }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: MC.forest }} />
            {copy.planCard.updated}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 6 }}>
          <span style={{ fontFamily: SERIF, fontSize: 62, lineHeight: 0.9, fontWeight: 500, color: MC.forest }}>78%</span>
          <span style={{ fontSize: 15, lineHeight: 1.3, color: MC.muted, paddingBottom: 8 }}>
            {copy.planCard.funding}
            <br />
            {copy.planCard.probability}
          </span>
        </div>
        <p style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5, color: MC.faint2, margin: '0 0 12px', letterSpacing: '0.02em' }}>
          {copy.planCard.monteCarlo}
        </p>
        <div style={{ height: 7, borderRadius: 999, background: '#EAE3D4', overflow: 'hidden', marginBottom: 22 }}>
          <div style={{ ...bars('78%'), background: MC.forest }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[
            { label: copy.planCard.retire, chip: copy.planCard.secure, chipText: '#21463A', chipBg: '#DDE8E0', w: '92%', bar: MC.forestDeep },
            { label: copy.planCard.college, chip: `${copy.planCard.onTrack} · 94%`, chipText: '#2F5D4F', chipBg: '#E6EDE7', w: '94%', bar: MC.forest },
            { label: copy.planCard.move, chip: copy.planCard.atRisk, chipText: '#A85636', chipBg: '#F2E4DB', w: '46%', bar: MC.clay },
          ].map((row) => (
            <div key={row.label}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                <span style={{ fontSize: 15, fontWeight: 600, color: MC.ink }}>{row.label}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: row.chipText, background: row.chipBg, padding: '3px 10px', borderRadius: 999 }}>
                  {row.chip}
                </span>
              </div>
              <div style={{ height: 5, borderRadius: 999, background: '#EAE3D4', overflow: 'hidden' }}>
                <div style={{ ...bars(row.w), background: row.bar }} />
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 20, paddingTop: 15, borderTop: '1px solid #ECE5D7', display: 'flex', flexWrap: 'wrap', gap: '14px 18px' }}>
          {[
            [copy.planCard.secure, '#21463A'],
            [copy.planCard.onTrack, '#2F5D4F'],
            [copy.planCard.atRisk, '#A85636'],
            [copy.planCard.offTrack, '#9B3D2A'],
          ].map(([label, dot]) => (
            <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: MC.muted2 }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: dot }} />
              {label}
            </span>
          ))}
        </div>
        <div style={{ marginTop: 12, fontSize: 12, color: MC.faint2, letterSpacing: '0.02em' }}>
          {copy.planCard.computed}
        </div>
      </div>
    </div>
  )
}

export default function Hero() {
  const { language } = useMyCIOLanguage()
  const copy = getMyCIOCopy(language)
  const { idx, phase } = useHeroRotation(copy.hero.questions.length)
  const item = copy.hero.questions[idx] ?? copy.hero.questions[0]
  const h1Cls = phase === 'out' ? 'mc-hero-out' : 'mc-hero-in'
  const leadCls = phase === 'out' ? 'mc-hero-lead-out' : 'mc-hero-lead-in'

  return (
    <section
      className="mc-container mc-pad-hero mc-gap64"
      style={{ maxWidth: 1180, margin: '0 auto', padding: '84px 32px 64px', display: 'flex', gap: 64, alignItems: 'center', flexWrap: 'wrap' }}
    >
      <div className="mc-fadeup" style={{ flex: '1 1 480px', minWidth: 320 }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 9,
            padding: '7px 14px',
            border: `1px solid ${MC.border}`,
            borderRadius: 999,
            background: MC.surface,
            marginBottom: 30,
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: 999, background: MC.forest }} />
          <span style={{ fontSize: 12.5, letterSpacing: '0.04em', color: MC.muted, fontWeight: 500 }}>
            {copy.hero.badge}
          </span>
        </div>

        {/* Rotating H1 — key forces the dissolve animation to restart on each phase/index change. */}
        <div style={{ position: 'relative', margin: '0 0 22px' }}>
          <h1
            key={`h1-${idx}-${phase}`}
            className={`mc-h1 ${h1Cls}`}
            style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 78, lineHeight: 1.0, letterSpacing: '-0.025em', margin: 0, color: MC.ink, textWrap: 'balance' }}
          >
            {item.a}
            <em style={{ fontStyle: 'italic', color: MC.forest }}>{item.em}</em>
          </h1>
        </div>

        <p style={{ fontFamily: SERIF, fontSize: 23, lineHeight: 1.35, color: MC.forest, margin: '0 0 18px', fontStyle: 'italic' }}>
          {copy.hero.sub}
        </p>
        <p
          key={`lead-${idx}-${phase}`}
          className={leadCls}
          style={{ fontSize: 18.5, lineHeight: 1.62, color: MC.muted, margin: '0 0 34px', maxWidth: 490, textWrap: 'pretty' }}
        >
          {item.lead}
        </p>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 22 }}>
          <Link
            href="/sign-in#signup"
            className="mc-cta-primary"
            style={{ fontSize: 16, fontWeight: 600, color: MC.bg, background: MC.forest, padding: '15px 26px', borderRadius: 999, textDecoration: 'none' }}
          >
            {copy.hero.primaryCta}
          </Link>
          <a
            href="#how"
            className="mc-cta-secondary"
            style={{ fontSize: 16, fontWeight: 600, color: MC.ink, background: 'transparent', border: '1px solid #C9C2B2', padding: '14px 26px', borderRadius: 999, textDecoration: 'none' }}
          >
            {copy.hero.secondaryCta}
          </a>
        </div>
        <p style={{ fontSize: 13.5, color: MC.faint, margin: 0, letterSpacing: '0.01em', fontFamily: SANS }}>{copy.hero.micro}</p>
      </div>

      <PlanProofCard copy={copy} />
    </section>
  )
}
